import { NavLink, Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { all_routes } from "../../../../router/all_routes";
import { useEffect, useMemo } from "react";
import useStudent from "../hooks/useStudent"; // see snippet below
import PageHeader from "components/Common/PageHeader";
import CustomButton from "components/Common/ui/CustomButton";
import { useAbility } from "config/abilityContext";

type OutletCtx = {
  student: ReturnType<typeof useStudent>["student"];
};

const StudentDetailsLayout = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const { fetchStudentById, student } = useStudent();
  const ability = useAbility();
  useEffect(() => {
    if (id) fetchStudentById(id);
  }, [id]);

  const outletContext = useMemo<OutletCtx>(() => ({ student }), [student]);

  if (!student) {
    return null;
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={
            student
              ? `${student.user?.firstName || ""} ${student.user?.lastName || ""}`.trim() || t("student_details")
              : t("student_details")
          }
          breadcrumbItems={[
            { label: t("dashboard"), path: all_routes.dashboard },
            { label: t("students"), path: all_routes.studentList },
            { label: t("student_details"), active: true },
          ]}
        />

        {
          ability.can("update", "Student") && (
            <div className="d-flex justify-content-end">
              <CustomButton asLink to={all_routes.editStudent.replace(":id", student.id)} className="">
                <i className="ti ti-edit-circle me-2" />
                {t("edit_student")}
              </CustomButton>
            </div>
          )
        }

        <div className="row">
          <div className="col-md-12">
            <ul className="nav nav-tabs nav-tabs-bottom mb-4">
              {ability.can("getAll", "Student") && (
                <li>
                  <NavLink
                    to={
                      id
                        ? `${routes.studentDetails.replace(":id", id)}${routes.studentDetailsTabs.details}`
                        : "#"
                    }
                    className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                  >
                    <i className="ti ti-school me-2" /> {t("details")}
                  </NavLink>
                </li>
              )}

              {ability.can("getAll", "Fee") && (
                <li>
                  <NavLink
                    to={
                      id
                        ? `${routes.studentDetails.replace(":id", id)}${routes.studentDetailsTabs.fees}`
                        : "#"
                    }
                    className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                  >
                    <i className="ti ti-report-money me-2" /> {t("fees")}
                  </NavLink>
                </li>
              )}
              {ability.can("getAll", "Planner") && (
                <li>
                  <NavLink
                    to={
                      id
                        ? `${routes.studentDetails.replace(":id", id)
                        }${routes.studentDetailsTabs.routine}`
                        : "#"
                    }
                    className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                  >
                    <i className="ti ti-table me-2" /> {t("routine")}
                  </NavLink>
                </li>
              )}
              {ability.can("getAll", "Absence") && (
                <li>
                  <NavLink
                    to={
                      id
                        ? `${routes.studentDetails.replace(":id", id
                        )}${routes.studentDetailsTabs.absences}`
                        : "#"
                    }
                    className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                  >
                    <i className="ti ti-calendar-share me-2" /> {t("absences")}
                  </NavLink>
                </li>
              )}
              {ability.can("getAll", "Mark") && (
                <li>
                  <NavLink
                    to={
                      id
                        ? `${routes.studentDetails.replace(":id", id
                        )}${routes.studentDetailsTabs.marks}`
                        : "#"
                    }
                    className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                  >
                    <i className="ti ti-hexagonal-prism-plus me-2" /> {t("marks")}
                  </NavLink>
                </li>
              )}
              {ability.can("getAll", "Task") && (
                <li>
                  <NavLink
                    to={
                      id
                        ? `${routes.studentDetails.replace(":id", id
                        )}${routes.studentDetailsTabs.tasks}`
                        : "#"
                    }
                    className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                  >
                    <i className="ti ti-license me-2" /> {t("home_work")}
                  </NavLink>
                </li>
              )}
              {ability.can("getAll", "AuthorizedPickUpPerson") && (
                <li>
                  <NavLink
                    to={
                      id
                        ? `${routes.studentDetails.replace(":id", id
                        )}${routes.studentDetailsTabs.authorizedPickUpPerson}`
                        : "#"
                    }
                    className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                  >
                    <i className="ti ti-user-shield me-2" /> {t("authorizedPickUpPerson.title")}
                  </NavLink>
                </li>
              )}
              {ability.can("getAll", "AuthorizedPickUpPerson") && (
                <li>
                  <NavLink
                    to={
                      id
                        ? `${routes.studentDetails.replace(":id", id
                        )}${routes.studentDetailsTabs.studentExit}`
                        : "#"
                    }
                    className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                  >
                    <i className="ti ti-home-move me-2" /> {t("studentExit.title")}
                  </NavLink>
                </li>
              )}
            </ul>
          </div>
        </div>

        <Outlet context={outletContext} />
      </div>

    </div>
  );
};

export default StudentDetailsLayout;
